(() => {
  if (customElements.get('dongxi-delivery-note')) return;
  let currentCart = null;
  let pendingCart = null;

  const offerStateFromCodes = (values) => {
    if (!Array.isArray(values)) return { codesKnown: false, shippingOfferSelected: false };
    const codes = new Set();
    for (const value of values) {
      const code = typeof value === 'string' ? value : value?.code;
      if (typeof code !== 'string' || !code.trim()) {
        return { codesKnown: false, shippingOfferSelected: false };
      }
      codes.add(code.trim().toLowerCase());
    }
    // Selection is not proof of final shipping eligibility.
    return { codesKnown: true, shippingOfferSelected: codes.size === 1 && codes.has('sgship10') };
  };
  const snapshotFromElement = (element) => {
    let codes;
    try { codes = JSON.parse(element.dataset.cartEvent || '').cart?.discountCodes; } catch (_) {}
    return {
      total: Number(element.dataset.cartTotal),
      count: Number(element.dataset.cartCount),
      currency: element.dataset.cartCurrency,
      shippable: element.dataset.cartShippable === 'true',
      taxesIncluded: element.dataset.cartTaxesIncluded === 'true',
      dutiesIncluded: element.dataset.cartDutiesIncluded === 'true',
      ...offerStateFromCodes(codes),
    };
  };
  const validSnapshot = (cart) => cart && Number.isInteger(cart.total) && cart.total >= 0 &&
    Number.isInteger(cart.count) && cart.count >= 0 && typeof cart.currency === 'string';

  const applySnapshot = (cart) => {
    if (!validSnapshot(cart)) return false;
    // Older or partial rendered sections must not erase a verified code selection.
    if (!cart.codesKnown && currentCart?.codesKnown) return false;
    currentCart = cart;
    document.querySelectorAll('dongxi-delivery-note').forEach((note) => note.update?.(cart));
    return true;
  };

  class DeliveryNote extends HTMLElement {
    connectedCallback() {
      if (this.dataset.context === 'cart') {
        // Newly rendered drawer/footer sections are authoritative after cart changes.
        const snapshot = snapshotFromElement(this);
        queueMicrotask(() => { if (this.isConnected) applySnapshot(snapshot); });
      }
      this.update(currentCart || snapshotFromElement(this));
    }

    update(cart) {
      const progress = this.querySelector('[data-delivery-progress]');
      if (!progress) return;
      const threshold = Number(this.dataset.threshold);
      const canShow = validSnapshot(cart) && this.dataset.exactRate === 'true' &&
        this.dataset.rateCurrency === cart.currency && threshold > 0 && cart.count > 0 &&
        cart.shippable && !cart.taxesIncluded && !cart.dutiesIncluded;
      const now = Date.now() / 1000;
      const offerStart = Number(this.dataset.shippingOfferStart);
      const offerEnd = Number(this.dataset.shippingOfferEnd);
      const selectedOffer = canShow && this.dataset.context === 'cart' &&
        this.dataset.country === 'SG' && cart.currency === 'SGD' && cart.codesKnown &&
        cart.shippingOfferSelected && cart.total < threshold &&
        Number.isFinite(offerStart) && Number.isFinite(offerEnd) && offerStart > 0 &&
        now >= offerStart && now < offerEnd;
      const rate = this.querySelector('[data-delivery-rate]');
      if (rate) {
        const rateText = selectedOffer && rate.dataset.selectedOfferRate
          ? rate.dataset.selectedOfferRate
          : canShow && cart.total >= threshold ? rate.dataset.freeRate : rate.dataset.standardRate;
        if (rate.textContent.trim() !== rateText) rate.textContent = rateText;
      }
      progress.hidden = !canShow || selectedOffer;
      // The drawer progress bar (dongxi-delivery-bar.liquid) hides as a whole.
      if (this.dataset.variant === 'bar') this.hidden = !canShow || selectedOffer;
      if (!canShow) return;
      const remaining = Math.max(0, threshold - cart.total);
      let text = this.dataset.qualifiedMessage;
      if (remaining > 0) {
        const amount = `${this.dataset.moneyPrefix}${new Intl.NumberFormat(document.documentElement.lang || 'en', {
          minimumFractionDigits: 2, maximumFractionDigits: 2,
        }).format(remaining / 100)}`;
        text = this.dataset.remainingMessage.replace('__amount__', amount);
      }
      if (progress.textContent.trim() !== text) progress.textContent = text;
      this.classList.toggle('dx-delivery-note--qualified', remaining === 0);
      const fill = this.querySelector('[data-delivery-fill]');
      if (fill) {
        const percent = Math.max(0, Math.min(100, Math.round((cart.total / threshold) * 100)));
        fill.style.width = `${percent}%`;
        this.querySelector('[data-delivery-track]')?.setAttribute('aria-valuenow', String(percent));
        this.classList.toggle('dx-delivery-bar--qualified', remaining === 0);
      }
    }
  }
  customElements.define('dongxi-delivery-note', DeliveryNote);

  const snapshotFromCart = (cart) => {
    if (!cart || !Number.isInteger(cart.total_price) || !Number.isInteger(cart.item_count) || typeof cart.currency !== 'string') return null;
    const offerState = offerStateFromCodes(cart.discount_codes);
    // Prefer authoritative rendered sections, then the existing shared cart refresh.
    if (!offerState.codesKnown) return null;
    return {
      ...offerState,
      total: cart.total_price, count: cart.item_count, currency: cart.currency,
      shippable: cart.requires_shipping === true,
      taxesIncluded: typeof cart.taxes_included === 'boolean' ? cart.taxes_included : Boolean(currentCart?.taxesIncluded),
      dutiesIncluded: typeof cart.duties_included === 'boolean' ? cart.duties_included : Boolean(currentCart?.dutiesIncluded),
    };
  };

  const refreshFromCart = () => {
    if (pendingCart) return pendingCart;
    // Reuse Dawn's shared full-cart request when the event only contains an added line.
    const request = typeof CartItems !== 'undefined' && typeof CartItems.fetchCartData === 'function'
      ? CartItems.fetchCartData()
      : fetch(`${window.routes.cart_url}.js`, { cache: 'no-store' }).then((response) => response.ok ? response.json() : null);
    pendingCart = request.then((cart) => {
      if (!applySnapshot(snapshotFromCart(cart))) {
        document.querySelectorAll('[data-delivery-progress]').forEach((node) => { node.hidden = true; });
      }
    }).catch(() => {
      document.querySelectorAll('[data-delivery-progress]').forEach((node) => { node.hidden = true; });
    }).finally(() => { pendingCart = null; });
    return pendingCart;
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cart = event.cartData;
      if (applySnapshot(snapshotFromCart(cart))) return;
      // Product add responses include fresh server-rendered cart sections. Reuse them
      // instead of another network request, including partial-stock additions.
      for (const html of Object.values(cart?.sections || {})) {
        if (typeof html !== 'string') continue;
        const section = new DOMParser().parseFromString(html, 'text/html');
        const note = section.querySelector('dongxi-delivery-note[data-context="cart"]');
        if (note && applySnapshot(snapshotFromElement(note))) return;
      }
      return refreshFromCart();
    });
  }
})();
